import {
  Row,
  Col,
  Input,
  InputNumber,
  AutoComplete,
  Select,
  Checkbox,
} from 'antd';
import { Form, WrapperSelect } from 'common/components';

import { useGetNutritionEnum } from '../../hook/useNutritionLabel'; // Import the hook to retrieve precision options
import { useGetSupplementPartOf } from '../../hook/useGetSupplementPartOf';
import { useFactPanelQasRequiredFields } from '../../hook/useFactPanelQasRequiredFields';

import {
  SUPPLEMENT_NUTRIENTS,
  DAILY_SYMBOL_SUGGESTIONS,
  SUPPLEMENT_NUTRIENT_UOM,
} from '../../constants';

import Messages from 'i18n/messages/product';
import { useIntl } from 'react-intl';
import { sortBy } from 'lodash';
import { QaSpecHelpContentWithoutLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';
import { TOOlTIP_FIELDS } from 'static/tooltipFields';

const formItemStyle = { marginBottom: 4 };

const SupplementNutrientForm = (props) => {
  const intl = useIntl();

  const { children, fieldName, namePath = [], productId, typeView } = props;

  const { partOfList, currentNutrientData } = useGetSupplementPartOf({
    fieldName,
    namePath,
  });

  const { measurementPrecisionCodeEnum, dailyPrecisionCodeEnum } =
    useGetNutritionEnum(); // Fetch precision options
  const { getRequiredRule } = useFactPanelQasRequiredFields({
    productId,
    typeView,
    section: 'qaSpecification.factsPanel.supplementLabel',
  });

  const isQaSpecType = typeView === 'qa-spec';

  const getNutrientSuggestions = () => {
    return sortBy(SUPPLEMENT_NUTRIENTS).reduce((accumulator, nutrient) => {
      if (nutrient === 'Calories') return accumulator;
      if (nutrient === 'Calories from Saturated Fat') return accumulator;

      return [...accumulator, { label: nutrient, value: nutrient }];
    }, []);
  };

  const nutrientSuggestions = getNutrientSuggestions();

  return (
    <div className='supplement-form__add-nutrient-form'>
      <Form.Item
        name={[...namePath, 'nutritionName']}
        style={formItemStyle}
        rules={[
          {
            required: true,
            message: 'Name is required',
          },
        ]}
      >
        <QaSpecHelpContentWithoutLabel
          fieldPath={TOOlTIP_FIELDS.nutrientNutritionName.fieldPath}
          isEnabled={isQaSpecType}
        >
          <AutoComplete
            options={nutrientSuggestions}
            filterOption={(inputValue, option) => {
              return (
                option?.label
                  ?.toLowerCase()
                  ?.indexOf(inputValue?.toLowerCase()) > -1
              );
            }}
            getPopupContainer={(triggerNode) => {
              return triggerNode;
            }}
          >
            <Input.TextArea
              placeholder={intl.formatMessage(Messages.supplementNutrientName)}
            />
          </AutoComplete>
        </QaSpecHelpContentWithoutLabel>
      </Form.Item>
      <Form.Item
        name={[...namePath, 'ingredientsPartOf']}
        style={formItemStyle}
      >
        <QaSpecHelpContentWithoutLabel
          fieldPath={TOOlTIP_FIELDS.nutrientIngredientsPartOf.fieldPath}
          isEnabled={isQaSpecType}
        >
          <WrapperSelect
            placeholder={intl.formatMessage(Messages.supplementPartOf)}
            getPopupContainer={(triggerNode) => {
              return triggerNode;
            }}
          >
            {partOfList.map((partOfItem) => {
              return (
                <Select.Option value={partOfItem} key={partOfItem}>
                  {partOfItem}
                </Select.Option>
              );
            })}
          </WrapperSelect>
        </QaSpecHelpContentWithoutLabel>
      </Form.Item>

      <Form.Item
        name={[...namePath, 'descriptor']}
        style={formItemStyle}
        rules={[
          getRequiredRule({
            fieldName: ['nutrients', 'descriptor'],
            displayName: intl.formatMessage(Messages.supplementDescriptor),
          }),
        ]}
      >
        <QaSpecHelpContentWithoutLabel
          fieldPath={TOOlTIP_FIELDS.nutrientDescriptor.fieldPath}
          isEnabled={isQaSpecType}
        >
          <Input.TextArea
            placeholder={intl.formatMessage(Messages.supplementDescriptor)}
          />
        </QaSpecHelpContentWithoutLabel>
      </Form.Item>

      {/* New Quantity Precision Dropdown */}
      <Form.Item
        name={[...namePath, 'quantityPrecision']}
        style={formItemStyle}
        initialValue='Exact' // Default to "Exact"
        rules={[
          getRequiredRule({
            fieldName: ['nutrients', 'quantityPrecision'],
            displayName: 'Quantity Precision',
          }),
        ]}
      >
        <QaSpecHelpContentWithoutLabel
          fieldPath={TOOlTIP_FIELDS.nutrientQuantityPrecision.fieldPath}
          isEnabled={isQaSpecType}
        >
          <WrapperSelect
            placeholder='Select Quantity Precision'
            showSearch
            filterOption={(input, option) =>
              option?.value?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
            }
            getPopupContainer={(triggerNode) => triggerNode}
          >
            {sortBy(measurementPrecisionCodeEnum?.[0]?.enumProperties, [
              'enumDisplayName',
              'enumGroup',
            ])?.map((item, index) => (
              <Select.Option value={item.enumDisplayName} key={index}>
                {item.enumDisplayName}
              </Select.Option>
            ))}
          </WrapperSelect>
        </QaSpecHelpContentWithoutLabel>
      </Form.Item>

      <Row wrap={false}>
        <Col span={14}>
          <Form.Item
            name={[...namePath, 'quantity']}
            style={formItemStyle}
            rules={[
              {
                required: true,
                message: 'Quantity is required',
              },
            ]}
          >
            <QaSpecHelpContentWithoutLabel
              fieldPath={TOOlTIP_FIELDS.nutrientQuantity.fieldPath}
              isEnabled={isQaSpecType}
            >
              <InputNumber
                min={0}
                placeholder={intl.formatMessage(Messages.supplementQuantity)}
              />
            </QaSpecHelpContentWithoutLabel>
          </Form.Item>
        </Col>
        <Col span={10}>
          <Form.Item
            name={[...namePath, 'quantityUom']}
            style={formItemStyle}
            rules={[
              {
                required: true,
                message: 'Unit is required',
              },
            ]}
          >
            <QaSpecHelpContentWithoutLabel
              fieldPath={TOOlTIP_FIELDS.nutrientQuantityUom.fieldPath}
              isEnabled={isQaSpecType}
            >
              <Select
                getPopupContainer={(triggerNode) => {
                  return triggerNode;
                }}
                allowClear
              >
                {SUPPLEMENT_NUTRIENT_UOM.map((UNIT) => {
                  return (
                    <Select.Option key={UNIT} value={UNIT}>
                      {UNIT}
                    </Select.Option>
                  );
                })}
              </Select>
            </QaSpecHelpContentWithoutLabel>
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name={[...namePath, 'quantityDescription']}
        style={formItemStyle}
        rules={[
          getRequiredRule({
            fieldName: ['nutrients', 'quantityDescription'],
            displayName: intl.formatMessage(
              Messages.supplementQuantityDescription
            ),
          }),
        ]}
      >
        <QaSpecHelpContentWithoutLabel
          fieldPath={TOOlTIP_FIELDS.nutrientQuantityDescription.fieldPath}
          isEnabled={isQaSpecType}
        >
          <Input
            placeholder={intl.formatMessage(
              Messages.supplementQuantityDescription
            )}
          />
        </QaSpecHelpContentWithoutLabel>
      </Form.Item>

      <Form.Item
        name={[...namePath, 'dailyValuePrecision']}
        style={formItemStyle}
        initialValue='Exact' // Default to "Exact"
        rules={[
          getRequiredRule({
            fieldName: ['nutrients', 'dailyValuePrecision'],
            displayName: 'Daily Value Precision',
          }),
        ]}
      >
        <QaSpecHelpContentWithoutLabel
          fieldPath={TOOlTIP_FIELDS.supplementDailyValuePrecision.fieldPath}
          isEnabled={isQaSpecType}
        >
          <WrapperSelect
            placeholder='Select Daily Value Precision'
            showSearch
            filterOption={(input, option) =>
              option?.value?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
            }
            getPopupContainer={(triggerNode) => triggerNode}
          >
            {sortBy(dailyPrecisionCodeEnum?.[0]?.enumProperties, [
              'enumDisplayName',
              'enumGroup',
            ])?.map((item, index) => (
              <Select.Option value={item.enumDisplayName} key={index}>
                {item.enumDisplayName}
              </Select.Option>
            ))}
          </WrapperSelect>
        </QaSpecHelpContentWithoutLabel>
      </Form.Item>

      <Row>
        <Col span={14}>
          <Form.Item
            name={[...namePath, 'dailyValue']}
            style={formItemStyle}
            rules={[
              {
                required: !currentNutrientData?.ingredientsPartOf,
                message: 'Daily Value is required',
              },
            ]}
          >
            <QaSpecHelpContentWithoutLabel
              fieldPath={TOOlTIP_FIELDS.nutrientDailyValue.fieldPath}
              isEnabled={isQaSpecType}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                placeholder={intl.formatMessage(Messages.supplementDailyValue)}
                addonAfter={'%'}
              />
            </QaSpecHelpContentWithoutLabel>
          </Form.Item>
        </Col>
        <Col span={10}>
          <Form.Item
            name={[...namePath, 'dailySymbol']}
            style={formItemStyle}
            rules={[
              getRequiredRule({
                fieldName: ['nutrients', 'dailySymbol'],
                displayName: intl.formatMessage(Messages.supplementDailySymbol),
              }),
            ]}
          >
            <QaSpecHelpContentWithoutLabel
              fieldPath={TOOlTIP_FIELDS.nutrientDailySymbol.fieldPath}
              isEnabled={isQaSpecType}
            >
              <AutoComplete
                allowClear
                options={DAILY_SYMBOL_SUGGESTIONS}
                placeholder={intl.formatMessage(Messages.supplementDailySymbol)}
                getPopupContainer={(triggerNode) => {
                  return triggerNode;
                }}
              />
            </QaSpecHelpContentWithoutLabel>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name={[...namePath, 'italicNutrient']}
            valuePropName='checked'
            style={formItemStyle}
            rules={[
              getRequiredRule({
                fieldName: ['nutrients', 'italicNutrient'],
                displayName: intl.formatMessage(Messages.supplementItalicName),
              }),
            ]}
          >
            <QaSpecHelpContentWithoutLabel
              fieldPath={TOOlTIP_FIELDS.nutrientItalicNutrient.fieldPath}
              isEnabled={isQaSpecType}
            >
              <Checkbox>
                {intl.formatMessage(Messages.supplementItalicName)}
              </Checkbox>
            </QaSpecHelpContentWithoutLabel>
          </Form.Item>
        </Col>
        <Col span={16}>
          <Form.Item
            name={[...namePath, 'italicDescriptor']}
            valuePropName='checked'
            style={formItemStyle}
            rules={[
              getRequiredRule({
                fieldName: ['nutrients', 'italicDescriptor'],
                displayName: intl.formatMessage(
                  Messages.supplementItalicDescriptor
                ),
              }),
            ]}
          >
            <QaSpecHelpContentWithoutLabel
              fieldPath={TOOlTIP_FIELDS.nutrientItalicDescriptor.fieldPath}
              isEnabled={isQaSpecType}
            >
              <Checkbox>
                {intl.formatMessage(Messages.supplementItalicDescriptor)}
              </Checkbox>
            </QaSpecHelpContentWithoutLabel>
          </Form.Item>
        </Col>
      </Row>
      {children && children}
    </div>
  );
};

export default SupplementNutrientForm;
